import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Observable, Subscription, switchMap, take } from 'rxjs';
import { MessageService } from 'primeng/api';

import { MfaModel, MfaTypesEnum, MfaStatusTypes } from '../../../Core/Users/models/mfa.model';
import { MfaService } from '../../services/mfa.service';
import { AbstractDialogComponent } from '../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { LoginApi } from '../../../api/login/login.api';
import { UsersApi } from '../../../api/users/users.api';


@Component({
    selector: 'agpr-mfa-setup',
    styleUrls: ['./mfa-setup.component.scss'],
    templateUrl: './mfa-setup.component.html',
    standalone: false
})

export class MfaSetupComponent extends AbstractDialogComponent implements OnDestroy {
    @Input() showAsDialog: boolean = false;
    @Input() cancelLabel: string = 'Core.button.cancel';

    @Input() confirm: (mfa: MfaModel) => Observable<any>;
    @Output() cancel: EventEmitter<void> = new EventEmitter<void>();

    isCodeInvalid: boolean;

    logoUrl: string;
    logoRouterLink = undefined;

    justSentPasswordReset: boolean = false;
    subscriptions: Subscription[] = [];

    isResellerDomain: boolean = false;

    readonly buttonTypes = ButtonTypesEnum;
    readonly mfaTypes = MfaTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    heading: string = this.localizationService.transform('Mfa.management.createNew.heading');
    showSetupRequiredMessage: boolean = false;
    selectedMfaType: MfaTypesEnum;
    selectedMfaMethod: MfaModel;
    email: string;
    qrCode: string;

    goToConfirm: boolean = false;

    sendConfirmationCode$ = this.currentStateUserService.getState().pipe(
        take(1),
        switchMap(state => {
            if (!state) {
                return this.loginApi.sendMfaEmailConfirmation(
                    this.selectedMfaMethod.email,
                    this.selectedMfaMethod.id,
                    this.mfaService.getLoginInfo().username
                );
            } else {
                return this.usersApi.sendMfaEmailConfirmation(this.selectedMfaMethod.toMfaVerifyModel());
            }
        })
    );

    allowedMfaSetupMethods: MfaModel[] = [];

    constructor(
        private localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public mfaService: MfaService,
        public currentStateUserService: CurrentUserStateService,
        public loginApi: LoginApi,
        public usersApi: UsersApi,
        private messageService: MessageService
    ) {
        super(coreDialogService, 'MfaSetupDialog');

        this.subscriptions.push(
            this.mfaService.mfaStatus$.subscribe(
                statusModel => {
                    if (statusModel?.allowedMfaMethods?.length > 0) {
                        this.allowedMfaSetupMethods = statusModel.allowedMfaMethods.map(x => new MfaModel(x)).sort((a, b) => a.type.localeCompare(b.type));
                        this.selectedMfaMethod = this.allowedMfaSetupMethods[0];
                    }
                    if (statusModel.status === MfaStatusTypes.MFA_SETUP_REQUIRED) {
                        this.heading = this.localizationService.transform('Mfa.setup.heading');
                        this.showSetupRequiredMessage = true;
                    }
                }
            )
        );

    }

    goToConfirmMfa() {
        if (this.selectedMfaMethod.type === MfaTypesEnum.email) {
            this.sendConfirmationCode$.subscribe({
                next: () => {
                    this.goToConfirm = true;
                    this.heading = this.localizationService.transform('Mfa.setup.confirm.heading');
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Mfa.setup.confirm.success'
                    });
                },
                error: (err) => {
                    if (err?.error?.details?.[0]?.message?.includes('Email already in use')) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'Mfa.setup.email.error',
                            detail: 'Mfa.setup.email.alreadyInUse',
                        });
                    } else {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'Mfa.setup.confirm.error',
                        });
                    }
                }
            });

        } else {
            this.goToConfirm = true;
            this.heading = this.localizationService.transform('Mfa.setup.confirm.heading');
        }
    }

    confirmMfa() {
        this.confirm(this.selectedMfaMethod).subscribe({
            next: () => {
                if (this.showAsDialog) {
                    this.closeDialog();
                }
            },
            error: () => {
                this.isCodeInvalid = true;
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
